import { ConditionalLink } from "components/conditionalLink";
import {
  DnDArea,
  File,
  UploadLabel,
} from "components/design/UploadFileSection";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { DownloadIcon } from "components/icons/Download";
import dayjs from "dayjs";
import { useAbortController } from "hooks/useAbortController";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  usePopulateBulkBillsMutation,
  useUploadBulkBillsMutation,
} from "store/apis/vendorBills";
import { BULK_ADD_BILLS_TEMPLATE_URL } from "types/contants/sampleExcelSheet";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";

export const BulkBillUpload = ({ isOpen, close }: ModalProps) => {
  const entityId = useCurrentEntityId();
  const {
    alertToast,
    successToast,
    info: infoToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [uploadFile, uploadState] = useUploadBulkBillsMutation();
  const [addBill] = usePopulateBulkBillsMutation();
  const { link } = useConstructInternalLink();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    {
      skip: !entityId,
    }
  );

  const uploadController = useAbortController({
    onAbort: () => {
      alertToast({ message: "Upload cancelled" });
    },
  });

  const addingRefController = useAbortController({
    onAbort: () => infoToast({ title: "Bills addition cancelled" }),
  });

  const onClose = () => {
    close();
    setFile(null);
    uploadState.reset();
  };

  const onDrop = async (files: File[]) => {
    if (!files.length) return;
    try {
      setFile(files[0]);
      await uploadFile({
        entityId,
        file: files[0],
        signal: uploadController.signal,
      }).unwrap();
    } catch (error) {
      setFile(null);
      if (
        (
          error as {
            status: string;
          }
        ).status === "FETCH_ERROR"
      )
        return;

      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as Error
      );
    }
  };

  const onAddBills = async ({ fileId }: { fileId: string }) => {
    if (!fileId) {
      alertToast({ message: "No file uploaded" });
      return;
    }
    const loadingtoastId = loadingToast({
      title: "Adding Bills",
    });
    close();
    uploadState.reset();
    try {
      await addBill({
        entityId,
        bulk_file_id: fileId,
        signal: addingRefController.signal,
      }).unwrap();
      successToast({ title: "Bills added successfully" });
      setFile(null);
    } catch (error) {
      setFile(null);
      if (
        (
          error as {
            status: string;
          }
        ).status === "FETCH_ERROR"
      )
        return;
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as Error
      );
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>Import Bills</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-mb-1.5">
            <UploadLabel>
              Download the pre-formatted template to ensure your data is
              properly structured. Add the bill details to the template and
              upload it.
            </UploadLabel>
          </div>

          <div className="t-flex t-flex-col t-gap-5">
            <span>
              <Button
                size="small"
                onClick={() => window.open(BULK_ADD_BILLS_TEMPLATE_URL)}
              >
                <DownloadIcon />
                <span className="t-ml-1.5">Download Excel Template</span>
              </Button>
            </span>
            {file ? (
              <File
                file={file}
                onCancel={uploadController.abort}
                onDelete={() => setFile(null)}
                isUploading={uploadState.isLoading}
                isUploaded={uploadState.isSuccess}
              />
            ) : (
              <DnDArea
                onDrop={onDrop}
                multiple={false}
                accept={{
                  "application/vnd.ms-excel": [".xls"],
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    [".xlsx"],
                }}
                subText="File Format: xls/xlsx"
              />
            )}

            {bookkeepingSettings?.accounting_lock_date && (
              <Banner
                type="neutral"
                title={
                  <span>
                    Your financials are locked until{" "}
                    {dayjs(bookkeepingSettings.accounting_lock_date).format(
                      "MMM DD, YYYY"
                    )}
                  </span>
                }
                description={
                  <span>
                    If any bills are dated before this, the upload will fail.
                    You can either change the bill dates manually, either update
                    the lock date in{" "}
                    <ConditionalLink
                      to={link("/books/books-settings")}
                      className="t-text-text-30 !t-underline"
                    >
                      settings,
                    </ConditionalLink>{" "}
                    or remove bills from locked period and continue.
                  </span>
                }
              />
            )}
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button>Cancel</Button>
          </Modal.RawClose>
          <Button
            disabled={!uploadState.data?.bulk_file_id}
            customType="primary"
            onClick={() =>
              uploadState.data?.bulk_file_id &&
              onAddBills({ fileId: uploadState.data.bulk_file_id })
            }
          >
            Add Bills
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
